/**
 * Inverse-variance meta-analysis: fixed effect and DerSimonian–Laird random effects.
 *
 * Every study arrives as an effect yᵢ with a sampling variance vᵢ on an additive scale.
 * Ratios (odds, risk, hazard) are pooled as natural logs and exponentiated for display.
 *
 * - Fixed effect: wᵢ = 1/vᵢ, θ = Σwᵢyᵢ/Σwᵢ, SE = 1/√Σwᵢ.
 * - Heterogeneity: Q = Σwᵢ(yᵢ − θ)² on k − 1 df; I² = max(0, (Q − df)/Q);
 *   τ² = max(0, (Q − df)/C) with C = Σwᵢ − Σwᵢ²/Σwᵢ (DerSimonian & Laird 1986).
 * - Random effects: wᵢ* = 1/(vᵢ + τ²), pooled the same way.
 * - Prediction interval (k ≥ 3): θ* ± t(k − 2)·√(τ² + SE*²) — Higgins, Thompson &
 *   Spiegelhalter (2009), as in Borenstein et al. (2009).
 *
 * Effect sizes from raw data:
 * - Log odds ratio from events/totals, adding 0.5 to every cell of a table with a zero
 *   cell. A study with no events in either arm (or only events) carries no information
 *   about the odds ratio and is left out.
 * - Hedges' g from means, SDs and sizes: d over the pooled SD, times Hedges' exact
 *   small-sample correction J = Γ(df/2) / (√(df/2)·Γ((df − 1)/2)), with
 *   v = J²·[(n₁ + n₂)/(n₁n₂) + d²/(2(n₁ + n₂))] (Borenstein et al. 2009, ch. 4).
 *   The familiar approximation J ≈ 1 − 3/(4·df − 1) agrees to about four decimals
 *   once df passes 20; the exact form is what metafor uses.
 *
 * The tests reproduce Borenstein et al.'s worked examples (Tables 14.1 and 14.4) to the
 * four decimals metafor prints for them.
 */

import { parseNumberInput } from "@/lib/parse-number";
import {
  chiSquareUpper,
  normalUpper,
  studentTQuantile,
} from "@/lib/stats/distributions";
import { logGamma } from "@/lib/stats/special";
import {
  detectSeparator,
  splitLine,
} from "@/lib/tool-kit/stats-research/matrix";
import { type Interval, zFor } from "@/lib/tool-kit/stats-research/proportions";

const LINE_BREAK = /\r\n|\r|\n/;

export type EffectScale = "additive" | "ratio";

export interface StudyRow {
  /** The text before the numbers, or null when the line is numbers only. */
  label: string | null;
  values: number[];
  line: number;
}

export interface LineProblem {
  line: number;
  reason: StudyProblemReason;
}

export type StudyProblemReason =
  | "count"
  | "number"
  | "interval"
  | "positiveRatio"
  | "positiveSe"
  | "counts"
  | "noInformation"
  | "spread"
  | "size";

export interface StudyRows {
  rows: StudyRow[];
  problems: LineProblem[];
  headerSkipped: boolean;
}

function splitStudyLine(
  line: string,
  separator: ReturnType<typeof detectSeparator>,
  expected: number
): { label: string | null; numbers: string[] } | null {
  const cells = splitLine(line, separator);
  while (cells.length > 0 && cells.at(-1) === "") {
    cells.pop();
  }
  if (cells.length < expected) {
    return null;
  }
  const cut = cells.length - expected;
  const label = cells.slice(0, cut).join(" ").trim();
  return { label: label === "" ? null : label, numbers: cells.slice(cut) };
}

/**
 * One study per line: an optional label, then exactly `expected` numbers. A first line
 * whose numbers do not read is taken as a header row and skipped.
 */
export function parseStudyRows(text: string, expected: number): StudyRows {
  const separator = detectSeparator(text);
  const result: StudyRows = { rows: [], problems: [], headerSkipped: false };
  let seenLine = false;
  for (const [index, raw] of text.split(LINE_BREAK).entries()) {
    if (raw.trim() === "") {
      continue;
    }
    const line = index + 1;
    const first = !seenLine;
    seenLine = true;
    const split = splitStudyLine(raw, separator, expected);
    const values = split?.numbers.map(parseNumberInput) ?? [];
    if (split && values.every((value) => value !== null)) {
      result.rows.push({
        label: split.label,
        values: values as number[],
        line,
      });
      continue;
    }
    if (first) {
      result.headerSkipped = true;
      continue;
    }
    result.problems.push({ line, reason: split ? "number" : "count" });
  }
  return result;
}

export interface StudyEffect {
  label: string | null;
  line: number;
  /** Effect on the analysis scale (natural log for ratios). */
  yi: number;
  vi: number;
  /** 0.5 was added to each cell of this study's 2 × 2 table. */
  corrected: boolean;
}

export interface StudyEffects {
  effects: StudyEffect[];
  problems: LineProblem[];
}

type Converter = (
  values: number[]
) => { yi: number; vi: number; corrected?: boolean } | StudyProblemReason;

function convertRows(
  rows: readonly StudyRow[],
  convert: Converter
): StudyEffects {
  const out: StudyEffects = { effects: [], problems: [] };
  for (const row of rows) {
    const converted = convert(row.values);
    if (typeof converted === "string") {
      out.problems.push({ line: row.line, reason: converted });
      continue;
    }
    out.effects.push({
      label: row.label,
      line: row.line,
      yi: converted.yi,
      vi: converted.vi,
      corrected: converted.corrected ?? false,
    });
  }
  return out;
}

/** Effect and standard error. For a ratio, the SE is that of its natural log. */
export function effectsFromSe(
  rows: readonly StudyRow[],
  scale: EffectScale
): StudyEffects {
  return convertRows(rows, ([estimate, se]) => {
    if (!(se > 0)) {
      return "positiveSe";
    }
    if (scale === "ratio") {
      return estimate > 0
        ? { yi: Math.log(estimate), vi: se * se }
        : "positiveRatio";
    }
    return { yi: estimate, vi: se * se };
  });
}

/** Effect with its 95% interval; the SE is the interval's width over 2 × 1.96. */
export function effectsFromInterval(
  rows: readonly StudyRow[],
  scale: EffectScale
): StudyEffects {
  const z = zFor(0.95);
  return convertRows(rows, ([estimate, lower, upper]) => {
    if (!(lower < upper && lower <= estimate && estimate <= upper)) {
      return "interval";
    }
    if (scale === "ratio") {
      if (!(lower > 0)) {
        return "positiveRatio";
      }
      const se = (Math.log(upper) - Math.log(lower)) / (2 * z);
      return { yi: Math.log(estimate), vi: se * se };
    }
    const se = (upper - lower) / (2 * z);
    return { yi: estimate, vi: se * se };
  });
}

/** Log odds ratio and its variance from one study's events and totals. */
export function logOddsRatio(
  events1: number,
  total1: number,
  events2: number,
  total2: number
): { yi: number; vi: number; corrected: boolean } | StudyProblemReason {
  const counts = [events1, total1, events2, total2];
  if (
    counts.some((value) => !Number.isInteger(value) || value < 0) ||
    events1 > total1 ||
    events2 > total2
  ) {
    return "counts";
  }
  const cells = [events1, total1 - events1, events2, total2 - events2];
  const [a, b, c, d] = cells;
  if ((a === 0 && c === 0) || (b === 0 && d === 0)) {
    return "noInformation";
  }
  const corrected = Math.min(...cells) === 0;
  const [x, y, u, v] = corrected ? cells.map((cell) => cell + 0.5) : cells;
  return {
    yi: Math.log((x * v) / (y * u)),
    vi: 1 / x + 1 / y + 1 / u + 1 / v,
    corrected,
  };
}

export function effectsFromCounts(rows: readonly StudyRow[]): StudyEffects {
  return convertRows(rows, ([e1, n1, e2, n2]) => logOddsRatio(e1, n1, e2, n2));
}

export interface HedgesG {
  g: number;
  d: number;
  j: number;
  variance: number;
}

/** J(df) = Γ(df/2) / (√(df/2)·Γ((df − 1)/2)), the exact small-sample correction. */
export function hedgesCorrection(df: number): number {
  return Math.exp(
    logGamma(df / 2) - 0.5 * Math.log(df / 2) - logGamma((df - 1) / 2)
  );
}

/** Hedges' g for group 1 minus group 2. */
export function hedgesG(
  mean1: number,
  sd1: number,
  n1: number,
  mean2: number,
  sd2: number,
  n2: number
): HedgesG | StudyProblemReason {
  if (!(sd1 > 0 && sd2 > 0)) {
    return "spread";
  }
  if (!(Number.isInteger(n1) && Number.isInteger(n2) && n1 >= 2 && n2 >= 2)) {
    return "size";
  }
  const df = n1 + n2 - 2;
  const pooled = Math.sqrt(((n1 - 1) * sd1 ** 2 + (n2 - 1) * sd2 ** 2) / df);
  const d = (mean1 - mean2) / pooled;
  const j = hedgesCorrection(df);
  const variance = j * j * ((n1 + n2) / (n1 * n2) + (d * d) / (2 * (n1 + n2)));
  return { g: j * d, d, j, variance };
}

export function effectsFromMeans(rows: readonly StudyRow[]): StudyEffects {
  return convertRows(rows, ([m1, s1, n1, m2, s2, n2]) => {
    const result = hedgesG(m1, s1, n1, m2, s2, n2);
    return typeof result === "string"
      ? result
      : { yi: result.g, vi: result.variance };
  });
}

export interface PooledEstimate extends Interval {
  estimate: number;
  se: number;
  z: number;
  p: number;
}

export interface MetaAnalysisResult {
  k: number;
  fixed: PooledEstimate;
  random: PooledEstimate;
  q: number;
  df: number;
  qP: number;
  /** I² as a percentage. */
  i2: number;
  h2: number;
  tau2: number;
  /** Σwᵢ and DerSimonian–Laird's C, for the working. */
  sumWeights: number;
  c: number;
  /** Each study's share of the total weight, in percent. */
  fixedWeights: number[];
  randomWeights: number[];
  prediction: Interval | null;
}

function pool(
  effects: readonly { yi: number }[],
  weights: readonly number[],
  z: number
): PooledEstimate {
  let sumW = 0;
  let sumWy = 0;
  for (const [i, effect] of effects.entries()) {
    sumW += weights[i];
    sumWy += weights[i] * effect.yi;
  }
  const estimate = sumWy / sumW;
  const se = Math.sqrt(1 / sumW);
  const statistic = estimate / se;
  return {
    estimate,
    se,
    lower: estimate - z * se,
    upper: estimate + z * se,
    z: statistic,
    p: Math.min(1, 2 * normalUpper(Math.abs(statistic))),
  };
}

function shares(weights: readonly number[]): number[] {
  const total = weights.reduce((sum, w) => sum + w, 0);
  return weights.map((w) => (100 * w) / total);
}

/** Null with fewer than two studies or a non-positive variance. */
export function poolStudies(
  effects: readonly { yi: number; vi: number }[],
  level = 0.95
): MetaAnalysisResult | null {
  const k = effects.length;
  if (k < 2 || effects.some((e) => !(e.vi > 0 && Number.isFinite(e.yi)))) {
    return null;
  }
  const z = zFor(level);
  const w = effects.map((e) => 1 / e.vi);
  const fixed = pool(effects, w, z);
  let q = 0;
  let sumW = 0;
  let sumW2 = 0;
  for (const [i, effect] of effects.entries()) {
    q += w[i] * (effect.yi - fixed.estimate) ** 2;
    sumW += w[i];
    sumW2 += w[i] ** 2;
  }
  const df = k - 1;
  const c = sumW - sumW2 / sumW;
  const tau2 = Math.max(0, (q - df) / c);
  const wStar = effects.map((e) => 1 / (e.vi + tau2));
  const random = pool(effects, wStar, z);
  let prediction: Interval | null = null;
  if (k >= 3) {
    const half =
      studentTQuantile(1 - (1 - level) / 2, k - 2) *
      Math.sqrt(tau2 + random.se ** 2);
    prediction = {
      lower: random.estimate - half,
      upper: random.estimate + half,
    };
  }
  return {
    k,
    fixed,
    random,
    q,
    df,
    qP: chiSquareUpper(q, df),
    i2: q > df ? (100 * (q - df)) / q : 0,
    h2: q / df,
    tau2,
    sumWeights: sumW,
    c,
    fixedWeights: shares(w),
    randomWeights: shares(wStar),
    prediction,
  };
}
